/* eslint-disable array-bracket-newline */
/**
 * A table with new pairs
 */
import { useCallback } from 'react';
import {
  matchPath,
  useLoaderData,
  useNavigate,
  useNavigation,
  useSearchParams,
} from 'react-router-dom';
import { useSelector } from 'react-redux';
import { fromBase64, toBase64 } from 'js-base64';
import Header from '../MainPage/Header';
import Modal from '../../components/Modal';
import TickersDisplayBar from '../../components/TickersDisplayBar';
import Notifs from '../../components/Notifications';
import {
  TableFilterOption,
  TableFilterSelect,
} from '../../components/TableComponents/Filter';
import { IStore } from '../../reducers';
import { getAllUrlParams } from '../../helpers/url';
import useDidUpdateEffect from '../../hooks/useDidUpdate';
import useDevice from '../MainPage/useMobileDetect';
import { ITableSort } from '../../molecules/Table/DesktopTable';
import NewPairsBodyMobile from './mobile/NewPairsBody';
import NewPairsBody from './NewPairsBody';

export const defaultNewPairstSort = toBase64(
  JSON.stringify({ id: 'pool_created', isSortedDesc: true }),
  true
);

const liquidityFilterOptions: TableFilterOption[] = [
  { label: '> 0$', value: '1' },
  { label: '> 5$k', value: '5000' },
  { label: '> 10$k', value: '10000' },
];

const NewPairsPage = () => {
  const [search, setSearch] = useSearchParams();
  const navigate = useNavigate();
  const navigation = useNavigation();

  const { tickers } = useLoaderData() as Record<string, unknown>;

  const { isMobile } = useDevice();

  const plan = useSelector((state: IStore) => state.features.currentPlan);
  const rates = useSelector((state: IStore) => state.rates.items);

  const chain = search.get('chain') || 'ETH';
  const page = search.get('page') || '1';
  const liquidity = search.get('liquidity');
  const sort = search.get('sort') || defaultNewPairstSort;

  const isNavigationOnCurrentRoute = matchPath(
    '/new-pairs',
    navigation.location?.pathname ?? ''
  );

  const loading = navigation.state === 'loading' && isNavigationOnCurrentRoute;

  const onSetChain = (c: string) => {
    const params = getAllUrlParams();
    setSearch({ ...params, chain: c, page: '1' });
  };

  const onSetPage = (p: number) => {
    const params = getAllUrlParams();
    setSearch({ ...params, page: p.toString() });
  };

  const onLiquidityFilterSelect: TableFilterSelect = (value) => {
    const params = getAllUrlParams();
    if (!value) {
      const { liquidity, ...rest } = params;
      return setSearch({ ...rest });
    }
    return setSearch({ ...params, liquidity: value });
  };

  const handleSort = useCallback(
    (id: string) => {
      const currentSort = JSON.parse(fromBase64(sort));
      const params = getAllUrlParams();
      if (currentSort.id === id) {
        const newSort = toBase64(
          JSON.stringify({ id, isSortedDesc: !currentSort.isSortedDesc }),
          true
        );
        setSearch({ ...params, sort: newSort });
      } else {
        const newSort = toBase64(
          JSON.stringify({ id, isSortedDesc: true }),
          true
        );
        setSearch({ ...params, sort: newSort });
      }
    },
    [setSearch, sort]
  );

  const handleMobileSort = useCallback(
    (newSort: ITableSort) => {
      const params = getAllUrlParams();
      setSearch({ ...params, sort: toBase64(JSON.stringify(newSort), true) });
    },
    [setSearch]
  );

  useDidUpdateEffect(() => {
    const search = new URLSearchParams({ chain, page, sort }).toString();
    navigate({ pathname: '.', search }, { replace: true });
  }, [plan]);

  if (isMobile) {
    return (
      <>
        <NewPairsBodyMobile
          tickers={tickers}
          rates={rates}
          chain={chain}
          page={page}
          plan={plan}
          liquidityFilterOptions={liquidityFilterOptions}
          liquidity={liquidity}
          loading={loading}
          sort={sort}
          onSetChain={onSetChain}
          onSetPage={onSetPage}
          onLiquidityFilterSelect={onLiquidityFilterSelect}
          handleSort={handleMobileSort}
        />
        <Notifs />
        <Modal />
      </>
    );
  }

  return (
    <div className='newPairsPage'>
      <TickersDisplayBar />
      <Header chartPicker={false} />
      <NewPairsBody
        tickers={tickers}
        rates={rates}
        chain={chain}
        page={page}
        plan={plan}
        liquidityFilterOptions={liquidityFilterOptions}
        liquidity={liquidity}
        loading={loading}
        sort={sort}
        onSetChain={onSetChain}
        onSetPage={onSetPage}
        onLiquidityFilterSelect={onLiquidityFilterSelect}
        handleSort={handleSort}
      />
      <Notifs />
      <Modal />
    </div>
  );
};

export default NewPairsPage;
